<template>
  <div class="iuf">
    <input
      :id="inputId"
      ref="picker"
      type="file"
      class="iuf__native"
      :accept="accept"
      @change="onPick"
    />

    <!-- current state: new selection (pending) wins over the saved image -->
    <div v-if="shownSrc" class="iuf__row iuf__box">
      <button
        type="button"
        class="iuf__thumb"
        :title="'Preview ' + label"
        @click="open = true"
      >
        <img :src="shownSrc" :alt="label" />
      </button>

      <div class="iuf__meta">
        <span class="iuf__name" :title="fileName">{{ fileName }}</span>
        <span class="iuf__hint">{{ modelValue ? "New file - not saved yet" : "Saved" }}</span>
      </div>

      <div class="iuf__actions">
        <button type="button" class="iuf__btn" @click="open = true">
          Preview
        </button>
        <button type="button" class="iuf__btn" @click="choose">
          Change
        </button>
        <button
          v-if="modelValue"
          type="button"
          class="iuf__btn"
          @click="clear"
        >
          Remove
        </button>
      </div>
    </div>

    <div v-else class="iuf__row iuf__box">
      <button type="button" class="iuf__btn" @click="choose">
        Choose file
      </button>
      <span class="iuf__name iuf__name--empty">No file chosen</span>
    </div>

    <p v-if="error" class="iuf__error" role="alert">{{ error }}</p>

    <teleport to="body">
      <div
        v-if="open"
        class="iuf__overlay"
        role="dialog"
        aria-modal="true"
        :aria-label="label + ' preview'"
        @click.self="open = false"
        @keydown.esc="open = false"
      >
        <div class="iuf__dialog">
          <button type="button" class="iuf__close" aria-label="Close preview" @click="open = false">
            &times;
          </button>
          <img :src="shownSrc" :alt="label" />
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
let uid = 0;

export default {
  name: "ImageUploadField",
  props: {
    // base64 data URL of a newly chosen file (null when nothing new is chosen)
    modelValue: { type: String, default: null },
    // URL of the image already saved on the server (optional)
    savedUrl: { type: String, default: null },
    label: { type: String, default: "Image" },
    accept: { type: String, default: "image/png,image/jpeg,image/webp" },
    maxSizeMb: { type: Number, default: 2 },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      inputId: `iuf-${++uid}`,
      picked: "", // name of the newly chosen file
      open: false,
      error: "",
    };
  },
  computed: {
    shownSrc() {
      return this.modelValue || this.savedUrl || null;
    },
    fileName() {
      if (this.modelValue) return this.picked || "Selected image";
      return "Saved " + this.label.toLowerCase();
    },
  },
  watch: {
    // parent cleared the value (e.g. modal closed) -> forget the old name / error
    modelValue(v) {
      if (!v) {
        this.picked = "";
        this.error = "";
      }
    },
  },
  methods: {
    choose() {
      if (this.$refs.picker) this.$refs.picker.click();
    },
    clear() {
      this.error = "";
      this.$emit("update:modelValue", null);
    },
    onPick(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = ""; // lets the same file be picked again
      if (!file) return;

      const allowed = this.accept.split(",").map((t) => t.trim());
      if (!file.type.startsWith("image/") || (allowed.length && !allowed.includes(file.type))) {
        this.error = "Please choose a PNG, JPG or WEBP image.";
        return;
      }
      if (file.size > this.maxSizeMb * 1024 * 1024) {
        this.error = `Image must be ${this.maxSizeMb} MB or smaller.`;
        return;
      }

      this.error = "";
      const reader = new FileReader();
      reader.onload = () => {
        this.picked = file.name;
        this.$emit("update:modelValue", reader.result);
      };
      reader.onerror = () => {
        this.error = "Could not read this file. Please try another image.";
      };
      reader.readAsDataURL(file);
    },
  },
};
</script>

<style scoped>
/* visually hidden but still keyboard/label reachable */
.iuf__native {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

/* field box: same look as .ecu-m-input (border, radius, focus ring) */
.iuf__box {
  min-height: 44px;
  padding: 5px 6px;
  border: 1px solid #e3e6f0;
  border-radius: 12px;
  background: #fff;
  color: #1d2a4d;
  font-size: var(--ecu-fs-body);
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.iuf__box:focus-within {
  border-color: #f97316;
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.14);
}

.iuf__row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* same look as the themed native file button in .ecu-m-input[type="file"] */
.iuf__btn {
  height: 30px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: #fff1e6;
  color: #e84d00;
  font: inherit;
  font-size: var(--ecu-fs-body);
  font-weight: 700;
  line-height: 30px;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease;
}

.iuf__btn:hover {
  background: #f97316;
  color: #fff;
}

.iuf__btn:focus-visible {
  outline: 2px solid #f97316;
  outline-offset: 2px;
}

.iuf__thumb {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid #e3e6f0;
  border-radius: 8px;
  background: #f5f6fa;
  overflow: hidden;
  cursor: zoom-in;
}

.iuf__thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.iuf__meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1 1 120px;
}

.iuf__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  line-height: 1.3;
}

.iuf__name--empty {
  color: #5f6579;
}

.iuf__hint {
  font-size: 12px;
  color: #7a8096;
}

.iuf__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.iuf__error {
  margin: 6px 0 0;
  font-size: 12px;
  color: #dc2626;
}

.iuf__overlay {
  position: fixed;
  inset: 0;
  z-index: 20001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(2, 34, 40, 0.7);
}

.iuf__dialog {
  position: relative;
  max-width: min(92vw, 720px);
  max-height: 88vh;
  padding: 12px;
  background: #fff;
  border-radius: 10px;
}

.iuf__dialog img {
  display: block;
  max-width: 100%;
  max-height: calc(88vh - 24px);
  object-fit: contain;
  margin: 0 auto;
}

.iuf__close {
  position: absolute;
  top: -14px;
  right: -14px;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}
</style>